import { isPlatformBrowser } from '@angular/common';
import { Inject, Injectable, PLATFORM_ID } from '@angular/core';
import { Meta, Title } from '@angular/platform-browser';
import type { TabContent, TabInfo } from './tabbed-content';

/**
 * Type guard to check if a value is a SafeValue object
 * SafeValue objects have a private property 'changingThisBreaksApplicationSecurity'
 * that contains the actual HTML content
 */
interface SafeValue {
  changingThisBreaksApplicationSecurity?: string;
}

function isSafeValue(value: unknown): value is SafeValue {
  return (
    value !== null &&
    value !== undefined &&
    typeof value === 'object' &&
    'changingThisBreaksApplicationSecurity' in value
  );
}

@Injectable({
  providedIn: 'root',
})
export class TabbedContentSeoService {
  private isBrowser: boolean;

  constructor(private meta: Meta, private title: Title, @Inject(PLATFORM_ID) platformId: object) {
    this.isBrowser = isPlatformBrowser(platformId);
  }

  /**
   * Optimizes SEO metadata for tabbed content.
   * Structured data (JSON-LD) is handled exclusively by ServerSchemaService on the server
   * to produce a single consolidated <script type="application/ld+json"> in <head>.
   * Browser-side ld+json injection is intentionally omitted to prevent duplicates on CSR.
   */
  optimizeSeoMetadata(data: TabContent, activeTab?: TabInfo): void {
    if (!data) return;

    // Update meta description with tab content
    if (activeTab && activeTab.accordion?.accordionItems?.length) {
      this.updateMetaDescription(activeTab);
    }
  }

  /**
   * Updates meta description based on active tab content
   */
  private updateMetaDescription(activeTab: TabInfo): void {
    if (!activeTab.accordion?.accordionItems?.length) return;

    const description = activeTab.accordion.accordionItems
      .slice(0, 2) // Take first 2 items
      .map((item) => this.stripHtml(item.itemDescription || ''))
      .join(' ')
      .substring(0, 155); // Meta description should be under 160 chars

    if (description) {
      this.meta.updateTag({
        name: 'description',
        content: description + '...',
      });
    }
  }

  /**
   * Extracts raw HTML string from SafeValue objects
   * WysiwygRedactorService.bypassSanitizer() returns SafeHtml objects incorrectly cast as strings.
   * We access the internal property to get the actual HTML for text processing (SEO, accessibility).
   * Fixing the service would require refactoring 100+ components, which is outside the scope of this bugfix.
   */
  private extractHtmlFromSafeValue(html: unknown): string {
    if (!html) return '';

    // If it's already a string, return it
    if (typeof html === 'string') return html;

    // Use type guard to safely check for SafeValue
    if (isSafeValue(html) && html.changingThisBreaksApplicationSecurity) {
      return html.changingThisBreaksApplicationSecurity;
    }

    return '';
  }

  /**
   * Removes HTML tags from string
   * Handles both regular strings and SafeValue objects from Angular's DomSanitizer
   * SSR-safe: Works in both browser and server environments
   * @param html - Can be string, SafeHtml, or any legacy type from existing code
   */
  private stripHtml(html: unknown): string {
    const htmlString = this.extractHtmlFromSafeValue(html);
    if (!htmlString) return '';

    // SSR-safe HTML stripping
    if (this.isBrowser) {
      // Parse into a separate document so CMS markup never touches the live one. Scripts and event
      // handlers (e.g. <img onerror>) stay inert there.
      return new DOMParser().parseFromString(htmlString, 'text/html').body.textContent ?? '';
    } else {
      // Fallback for SSR: simple regex-based HTML stripping
      return htmlString.replace(/<[^>]*>/g, '');
    }
  }

  /**
   * Generates SEO-friendly URLs for tab content
   */
  generateTabUrl(tabTitle: string, index: number): string {
    const cleanTitle = this.stripHtml(tabTitle)
      .toLowerCase()
      .replace(/[^a-z0-9]/g, '-')
      .replace(/-+/g, '-')
      .replace(/^-|-$/g, '');

    return `#tab-${index}-${cleanTitle}`;
  }
}
